/// <reference types="vite/client" />
/**
 * TAMIX SAAS PLATFORM - Authentication API Service
 * ================================================
 * Generated: 2025-09-22 - Session 6 Sprint 0
 * API calls for authentication with Google OIDC
 */

import type { 
  LoginResponse, 
  User, 
  GoogleAuthResponse,
  AuthApiMethods 
} from '@/types/auth'

import { getApiV1Url } from '@/config/api'

class AuthApiService implements AuthApiMethods {
  private token: string | null = null

  private get baseURL(): string {
    return getApiV1Url()
  }

  // Set authentication token
  setToken(token: string): void {
    this.token = token
  }

  // Clear authentication token
  clearToken(): void {
    this.token = null
  }

  // Get authentication headers
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    return headers
  }

  // Handle API errors
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `HTTP ${response.status}: ${response.statusText}`
      }))
      
      throw new Error(errorData.detail || 'API request failed')
    }

    return response.json()
  }

  // Login with Email and Password
  async loginWithCredentials(credentials: { email: string; password: string }): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })

    const data = await this.handleResponse<{ access_token: string; refresh_token: string; token_type: string; expires_in: number }>(response)
    this.setToken(data.access_token)

    // Fetch current user details
    const user = await this.getCurrentUser()
    return {
      access_token: data.access_token,
      token_type: data.token_type,
      expires_in: data.expires_in,
      user,
    }
  }

  // Register new user
  async register(userData: {
    email: string
    password: string
    first_name: string
    last_name: string
    phone?: string
    job_title?: string
    tenant_id?: string
  }): Promise<{ status: string; message?: string; data?: any }> {
    const response = await fetch(`${this.baseURL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    })

    return this.handleResponse(response)
  }

  // Get Google OAuth login URL
  async getGoogleLoginUrl(): Promise<string> {
    const response = await fetch(`${this.baseURL}/auth/google/login`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Failed to get login URL')
    }

    const data = await response.json()
    return data.login_url
  }

  // Handle Google OAuth callback
  async handleGoogleCallback(code: string): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    })

    const data = await this.handleResponse<LoginResponse>(response)
    
    // Store token for future requests
    this.setToken(data.access_token)
    
    return data
  }

  // Refresh authentication token
  async refreshToken(currentToken: string): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
        'Content-Type': 'application/json',
      },
    })

    const data = await this.handleResponse<LoginResponse>(response)
    
    // Update stored token
    this.setToken(data.access_token)
    
    return data
  }

  // Get current authenticated user
  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${this.baseURL}/auth/me`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })

    return this.handleResponse<User>(response)
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      })
    } finally {
      // Always clear token, even if logout request fails
      this.clearToken()
    }
  }

  // Verify token is still valid
  async verifyToken(): Promise<boolean> {
    try {
      await this.getCurrentUser()
      return true
    } catch {
      return false
    }
  }

  // Parse Google OAuth response from URL
  parseGoogleAuthResponse(): GoogleAuthResponse | null {
    const urlParams = new URLSearchParams(window.location.search)
    
    const code = urlParams.get('code')
    const error = urlParams.get('error')
    const state = urlParams.get('state')
    const errorDescription = urlParams.get('error_description')

    if (code || error) {
      return {
        code: code || '',
        state: state || undefined,
        error: error || undefined,
        error_description: errorDescription || undefined,
      }
    }

    return null
  }

  // Handle authentication flow
  async handleAuthFlow(): Promise<LoginResponse | null> {
    const authResponse = this.parseGoogleAuthResponse()
    
    if (!authResponse) {
      return null
    }

    if (authResponse.error) {
      throw new Error(
        authResponse.error_description || 
        `Authentication error: ${authResponse.error}`
      )
    }

    if (authResponse.code) {
      return this.handleGoogleCallback(authResponse.code)
    }

    return null
  }
}

// Export singleton instance
export const authApi = new AuthApiService()

// Export class for testing
export { AuthApiService }